import { prioritizeOutlook, getOutlookClock } from "@revelate/calc/target-outlook";
import { getDealOwner, getFullName } from "@revelate/utils";
import { buildOutlookReadout } from "../target-outlook/readout";
import { selectForecastManagementPaths } from "./managementPolicy";
import { formatAnalysisMoney } from "../lib/moneyDisplay";
import type { useForecastExperience } from "./useForecastExperience";

/** Only adapts existing scoped decisions and monetary formatting for this caller. */
export function forecastManagementReadout(data: ReturnType<typeof useForecastExperience>, period: string) {
  if (!data.position || !data.story || data.outcome === null) return null;
  const { position, story } = data;
  const clock = getOutlookClock(data.asOf, data.range);
  const paths = clock.daysRemaining > 0 && data.model.expectedCloseV2CompanyForecast
    ? selectForecastManagementPaths(story, data.model.expectedCloseV2CompanyForecast,
      data.model.filteredEvaluatedForecastOpenDeals, data.range, position.target) : [];
  const decision = prioritizeOutlook({ current: data.outcome, target: position.target, closed: position.historical,
    futurePeriod: data.range.start > data.asOf, futureIncluded: data.complete, daysRemaining: clock.daysRemaining,
    cycle: data.futureHistory?.cycle ?? null, history: data.futureHistory, paths, newCreation: null,
    downsideDelta: story.scenarios.find(scenario => scenario.kind === "downside")?.delta ?? null });
  const deals = new Map(story.deals.map(deal => [deal.id, deal]));
  const open = new Map(data.model.filteredEvaluatedForecastOpenDeals.map(deal => [String(deal.id), deal]));
  const money = (value: number) => formatAnalysisMoney(value, data.model.companyCurrency);
  const gap = Math.max(0, position.target - data.outcome);
  const result = { ...buildOutlookReadout({ period, historical: position.historical, complete: data.complete, outlook: data.outcome,
    target: position.target, won: position.won, existing: position.expectedFromOpenDeals, future: data.future,
    gap, daysRemaining: clock.daysRemaining, decision, creation: null,
    opportunityName: id => deals.get(id)?.name ?? "Opportunity", ownerName: id => {
      const deal = open.get(id); const owner = deal ? getDealOwner(deal) : null;
      return owner ? getFullName(owner) : "Owner not assigned";
    }, formatMoney: money }), decision, gap };
  if (position.target > 0) {
    if (data.complete && !result.futurePrimary && result.decision.primaryFocus === "existing-deals"
      && paths.some(path => path.eventType === "moved-in")) {
      const pull = paths.filter(path => path.eventType === "moved-in").length;
      const secure = paths.length - pull;
      const focus = `Prioritise the ${paths.length} identified ${paths.length === 1 ? "opportunity" : "opportunities"}: ${[
        secure ? `secure ${secure} already scheduled within the period` : "",
        `separately assess bringing ${pull} forward from the following quarter`,
      ].filter(Boolean).join("; ")}.`;
      const previousFocus = result.packet.managementConclusions.focus;
      result.packet.managementConclusions.focus = focus;
      result.packet.recommendedFocus = focus;
      result.fallback = result.fallback.replace(previousFocus, focus);
    }
    return result;
  }
  // A missing company target must never become an above-target conclusion.
  const situation = `${period}: ${money(data.outcome)} ${data.complete ? "expected" : "from already won and existing pipeline"}.`;
  result.fallback = `${situation} A revenue target has not been configured for this company.`;
  result.packet.managementConclusions = { situation, materialUpside: "", recovery: "Target attainment cannot be assessed without a configured target.", focus: "", deprioritized: "" };
  result.packet.recommendedFocus = "A revenue target has not been configured.";
  result.packet.primaryFocus = "establish-outlook";
  result.packet.futurePipelineAnalysis = null;
  result.packet.recoveryAreas = [];
  result.packet.rankedMaterialActions = [];
  result.packet.decisionScope.recovery = "No above/below-target or recovery judgement is supported because the target is unavailable.";
  result.opportunities = [];
  result.futurePrimary = false;
  return result;
}
